import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import * as Yup from "yup";

import { VisualModelHelper } from "@/common/helpers/entity/vehicleVisualModel";
import { SvgHelper } from "@/common/helpers/svg";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { Mapper } from "@/common/mapping/mapper";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageDetectionDto,
  DamageDetectionItemDto,
  DamageDetectionMigrateToNewVisualModelDto,
  ItemDtoInDamageDetectionMigrateToNewVisualModelDto,
  VehicleArea,
  VehicleProjection,
  VehicleVisualModelDto,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../AppAlerts/NoDataAlert";
import AppButton from "../../Button/AppButton";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FieldValue from "../../Form/Display/FieldValue";
import AppTypography from "../../Text/AppTypography";
import VehicleDamageLink from "../VehicleDamage/VehicleDamageLink";
import VehicleVisualModelAutocomplete from "../VehicleVisualModel/VehicleVisualModelAutocomplete";
import { VisualModelViewer } from "../VehicleVisualModel/Viewer/v2/VisualModelViewer";

interface OwnProps {
  damageDetection: DamageDetectionDto;
  onCancel: () => void;
  onSave: (newValue: DamageDetectionDto) => void;
}

export type DamageDetectionMigrateToNewVisualModelProps = OwnProps;

export default function DamageDetectionMigrateToNewVisualModel({
  damageDetection,
  onCancel,
  onSave,
}: DamageDetectionMigrateToNewVisualModelProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();

  const vehicleVisualModelLatestOrDefaultRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsLatestOrDefaultGet,
    {
      vehicleId: damageDetection.vehicle?.id || undefined,
      isIgnoreDefaultForVehicle: true,
    },
    {
      deps: [damageDetection.vehicle?.id],
    },
  );
  const vehicleVisualModelLatestOrDefault = vehicleVisualModelLatestOrDefaultRequest?.data;

  const oldVehicleVisualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: damageDetection.vehicleVisualModel?.id || "",
    },
    {
      deps: [damageDetection.vehicleVisualModel?.id],
    },
  );
  const oldVehicleVisualModel = oldVehicleVisualModelRequest?.data;

  if (oldVehicleVisualModelRequest.isLoading || !oldVehicleVisualModelRequest.isFirstEnded) {
    return <LinearProgress />;
  }

  return (
    <Stack spacing={1}>
      <Alert severity='info'>
        To migrate a damage to new vehicle visual model, manually select new visual model and place
        damage point on correct position.
      </Alert>

      <Formik<
        BaseFormikValues &
          Omit<DamageDetectionMigrateToNewVisualModelDto, "items"> & {
            items?: Array<
              ItemDtoInDamageDetectionMigrateToNewVisualModelDto & {
                ddItem: DamageDetectionItemDto;
                feTempId?: string | null;
              }
            >;
            oldVehicleVisualModel: Nil<VehicleVisualModelDto>;
            newVehicleVisualModel: Nil<VehicleVisualModelDto>;
          }
      >
        enableReinitialize
        initialValues={{
          oldVehicleVisualModelId: damageDetection.vehicleVisualModel?.id,
          newVehicleVisualModelId:
            vehicleVisualModelLatestOrDefault &&
            vehicleVisualModelLatestOrDefault.id !== oldVehicleVisualModel?.id
              ? vehicleVisualModelLatestOrDefault.id
              : undefined,
          items: damageDetection?.items?.map((x) => ({
            id: x.id,
            oldPoint: x.point ? { ...x.point } : undefined,
            newPoint: x.point ? { ...x.point } : undefined,
            ddItem: x,
          })),
          isMigrateVehicleDamages: true,

          oldVehicleVisualModel: oldVehicleVisualModel,
          newVehicleVisualModel:
            vehicleVisualModelLatestOrDefault &&
            vehicleVisualModelLatestOrDefault.id !== oldVehicleVisualModel?.id
              ? vehicleVisualModelLatestOrDefault
              : undefined,

          submit: "",
        }}
        validationSchema={Yup.object().shape({
          // vehicleId: Yup.string().required("Vehicle is required"),
        })}
        onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
          try {
            const response =
              await apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdMigrateToNewVisualModelPost(
                {
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  damageDetectionId: damageDetection.id || "",
                  damageDetectionMigrateToNewVisualModelDto: {
                    oldVehicleVisualModelId: values.oldVehicleVisualModelId,
                    newVehicleVisualModelId: values.newVehicleVisualModelId,
                    items: values.items?.map((x) => ({
                      id: x.id,
                      oldPoint: x.oldPoint,
                      newPoint: x.newPoint,
                    })),
                    isMigrateVehicleDamages: values.isMigrateVehicleDamages,
                  },
                },
              );
            enqueueSnackbar("Saved.", {
              variant: "success",
            });
            onSave && onSave(response.data);

            if (mounted.current) {
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err: any) {
            if (mounted.current) {
              ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
              setStatus({ success: false });
              setSubmitting(false);
            }
          }
        }}
      >
        {({ errors, handleSubmit, isSubmitting, values, setFieldValue }) => {
          return (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Stack
                  sx={{
                    flexDirection: { xxs: "column", md: "row" },
                    gap: { xxs: 0, md: 2 },
                  }}
                >
                  <FormControl fullWidth margin='dense'>
                    <VehicleVisualModelAutocomplete
                      disabled
                      entity={values.oldVehicleVisualModel}
                      label='Old visual model'
                    />
                  </FormControl>

                  <FormControl fullWidth margin='dense'>
                    <VehicleVisualModelAutocomplete
                      entity={undefined}
                      entityId={values.newVehicleVisualModelId}
                      searchFilters={{
                        excludeIds: values.oldVehicleVisualModel?.id
                          ? [values.oldVehicleVisualModel.id]
                          : undefined,
                      }}
                      label='New visual model'
                      required
                      onChange={(newValue) => {
                        setFieldValue("newVehicleVisualModelId", newValue?.id);
                        setFieldValue("newVehicleVisualModel", newValue);
                      }}
                    />
                  </FormControl>
                </Stack>

                <Stack spacing={2}>
                  {/* Items */}
                  {values?.items?.map((item, i) => {
                    const oldVisualPoint =
                      Mapper.visualModel.mapDamageDetectionItemDtoToVisualModelPoint(item.ddItem, {
                        coords:
                          SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(
                            item.oldPoint,
                          ) || undefined,
                      });
                    const newVisualPoint = item.newPoint
                      ? Mapper.visualModel.mapDamageDetectionItemDtoToVisualModelPoint(
                          item.ddItem,
                          {
                            coords:
                              SvgHelper.mapGeneralSvgPointDtoToSvgPointFullCoordsOrNull(
                                item.newPoint,
                              ) || undefined,
                          },
                        )
                      : null;

                    return (
                      <Card key={i}>
                        <CardHeader title={`Item #${i + 1}`} />
                        <CardContent>
                          <Stack spacing={1}>
                            {/* Item details */}
                            <Stack
                              sx={{
                                flexDirection: { xxs: "column", md: "row" },
                                columnGap: { xxs: 1, md: 2 },
                              }}
                            >
                              <FieldValue label='Part & damage type' direction='column'>
                                <Box>
                                  {item.ddItem?.partType?.name} - {item.ddItem?.damageType?.name}
                                </Box>
                              </FieldValue>

                              <FieldValue label='Vehicle damage' direction='column'>
                                {item.ddItem?.damage && (
                                  <Typography
                                    component='div'
                                    variant='body2'
                                    color='text.secondary'
                                  >
                                    <VehicleDamageLink
                                      vehicleId={item.ddItem?.damage?.vehicleId}
                                      entity={item.ddItem?.damage}
                                      inlineProps={{ withIcon: false }}
                                    />
                                  </Typography>
                                )}
                              </FieldValue>

                              <FieldValue label='Area & Projection' direction='column'>
                                <InlineApiEnumValue type='VehicleArea' value={item.ddItem?.area} />{" "}
                                /{" "}
                                {(item.ddItem?.projection && (
                                  <InlineApiEnumValue
                                    type='VehicleProjection'
                                    value={item.ddItem?.projection}
                                  />
                                )) ||
                                  "-"}
                              </FieldValue>
                            </Stack>

                            <Stack
                              sx={{
                                flexDirection: { xxs: "column", md: "row" },
                                gap: { xxs: 2, md: 4 },
                              }}
                            >
                              {/* Old model */}
                              <Stack spacing={1}>
                                <Typography variant='h6'>Old visual model</Typography>

                                {!values.oldVehicleVisualModel && (
                                  <NoDataAlert title='Old visual model not found for this damage.' />
                                )}

                                <Box>
                                  {values.oldVehicleVisualModel && (
                                    <Box>
                                      <VisualModelViewer
                                        visualModelId={undefined}
                                        visualModel={values.oldVehicleVisualModel}
                                        points={oldVisualPoint ? [oldVisualPoint] : []}
                                        initialMainImageSelector={{
                                          area: oldVisualPoint?.meta.area || VehicleArea.Exterior,
                                          projection:
                                            oldVisualPoint?.meta.projection ||
                                            VehicleProjection.Front,
                                        }}
                                        variant='compact'
                                        direction='column'
                                        isFetchVisualModel={false}
                                        isSwitchImageEnabled={false}
                                        mainViewerProps={{
                                          svgViewerProps: {
                                            isZoomEnabled: true,
                                            isResizeEnabled: true,
                                            isPointDragEnabled: true,
                                            svgProps: {
                                              width: { xxs: 300, md: 500 },
                                              height: { xxs: 300, md: 500 },
                                              minWidth: { xxs: 100, md: 300 },
                                              minHeight: { xxs: 100, md: 300 },
                                              maxWidth: { xxs: 400, md: 500 },
                                              maxHeight: { xxs: 400, md: 500 },
                                              cursor: "default",
                                              point: {
                                                cursor: "default",
                                              },
                                            },
                                            defaultPointStyle: {
                                              ...VisualModelHelper.defaultSvgPointStyleForDamage,
                                            },
                                          },
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Stack>

                              {/* New model */}
                              <Stack spacing={1}>
                                <Typography variant='h6'>New visual model</Typography>

                                <Box>
                                  {values.newVehicleVisualModel && (
                                    <Box>
                                      <VisualModelViewer
                                        visualModelId={undefined}
                                        visualModel={values.newVehicleVisualModel}
                                        points={newVisualPoint ? [newVisualPoint] : []}
                                        initialMainImageSelector={{
                                          area: newVisualPoint?.meta.area || VehicleArea.Exterior,
                                          projection:
                                            newVisualPoint?.meta.projection ||
                                            VehicleProjection.Front,
                                        }}
                                        variant='compact'
                                        direction='column'
                                        isFetchVisualModel={false}
                                        isSwitchImageEnabled={false}
                                        mainViewerProps={{
                                          svgViewerProps: {
                                            isZoomEnabled: true,
                                            isResizeEnabled: true,
                                            isPointDragEnabled: true,
                                            svgProps: {
                                              width: { xxs: 300, md: 500 },
                                              height: { xxs: 300, md: 500 },
                                              minWidth: { xxs: 100, md: 300 },
                                              minHeight: { xxs: 100, md: 300 },
                                              maxWidth: { xxs: 400, md: 500 },
                                              maxHeight: { xxs: 400, md: 500 },
                                              cursor: "pointer",
                                              point: {
                                                cursor: "pointer",
                                              },
                                            },
                                            defaultPointStyle: {
                                              ...VisualModelHelper.defaultSvgPointStyleForDamage,
                                            },
                                          },
                                          onNewPointClicked: (params) => {
                                            setFieldValue(
                                              `items[${i}].newPoint`,
                                              SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(
                                                params.coords,
                                              ),
                                            );
                                          },
                                          onPointMoved: (params) => {
                                            setFieldValue(
                                              `items[${i}].newPoint`,
                                              SvgHelper.mapSvgPointFullCoordsToGeneralSvgPointDto(
                                                params.endCoords,
                                              ),
                                            );
                                          },
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Stack>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}

                  <FormControl margin='dense' fullWidth>
                    <FormControlLabel
                      sx={{ margin: 0 }}
                      disableTypography
                      control={
                        <Checkbox
                          checked={values.isMigrateVehicleDamages ?? false}
                          onChange={(e) => {
                            setFieldValue("isMigrateVehicleDamages", e.target.checked);
                          }}
                        />
                      }
                      label={
                        <AppTypography
                          decoration={{ variant: "helpText" }}
                          tooltipProps={{
                            title:
                              "Migrate for all vehicle damages detected in this damage detection and with the same visual model.",
                          }}
                        >
                          Migrate vehicle damages
                        </AppTypography>
                      }
                    />
                  </FormControl>
                </Stack>

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                <Stack
                  direction={{ xxs: "column", sm: "row" }}
                  gap={1}
                  sx={{
                    justifyContent: { xxs: "flex-start", sm: "flex-end" },
                    alignItems: { xxs: "flex-start", sm: "flex-start" },
                  }}
                >
                  <AppButton
                    variant='outlined'
                    size='medium'
                    color='text'
                    onClick={() => {
                      onCancel();
                    }}
                  >
                    Cancel
                  </AppButton>

                  <AppButton
                    loading={isSubmitting}
                    type='submit'
                    variant='contained'
                    size='medium'
                    color='primary'
                  >
                    Migrate
                  </AppButton>
                </Stack>
              </Stack>
            </form>
          );
        }}
      </Formik>
    </Stack>
  );
}
